import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  DragStartEvent,
  DragEndEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import Column from "./Column";
import SortableItem from "./SortableItem";
import "./App.css";

// Reference data for matching – each vector is in the fixed order of the 12 categories.
const referenceData = [
  { crisis: "War in Gaza", rankings: [12, 11, 9, 10, 4, 8, 6, 5, 7, 1, 2, 3] },
  {
    crisis: "Crisis in Somalia",
    rankings: [11, 12, 8, 10, 4, 9, 7, 6, 5, 1, 2, 3],
  },
  {
    crisis: "Crisis in Haiti",
    rankings: [12, 11, 8, 10, 5, 9, 7, 4, 6, 1, 2, 3],
  },
  {
    crisis: "Crisis in South Sudan",
    rankings: [11, 12, 8, 10, 4, 7, 6, 9, 5, 1, 2, 3],
  },
  {
    crisis: "Mining in Africa",
    rankings: [8, 10, 7, 1, 5, 11, 6, 12, 9, 2, 3, 4],
  },
  {
    crisis: "Human Rights in Iran",
    rankings: [7, 10, 8, 1, 9, 6, 11, 5, 12, 2, 3, 4],
  },
  {
    crisis: "Human Rights in China",
    rankings: [2, 9, 7, 1, 11, 6, 10, 8, 12, 3, 4, 5],
  },
  {
    crisis: "Human Rights in Uganda",
    rankings: [4, 8, 6, 9, 3, 5, 7, 10, 12, 11, 1, 2],
  },
  {
    crisis: "Amazon Rainforest",
    rankings: [5, 9, 7, 4, 11, 6, 8, 12, 10, 1, 2, 3],
  },
];

// Fixed order of the 12 issue categories.
export const categories = [
  "War and Violent Conflict",
  "Deprivation of Essential Needs",
  "Access to Education and Social Services",
  "Refugees and Displaced Peoples",
  "Religious and Ethnic Persecution",
  "Child Exploitation and Endangerment",
  "Women's Rights and Gender Equality",
  "Environmental Destruction and Climate Change",
  "Political Repression and Civil Liberties",
  "LGBTQ Rights",
  "Elder Abuse",
  "Disability Rights",
];

// Initial data: left ("todo") gets the first 6 categories; right ("done") gets the next 6; center ("in-progress") starts empty.
export type ColumnType = {
  id: string;
  title: string;
  items: string[];
};

const initialData: ColumnType[] = [
  {
    id: "todo",
    title: "To Do",
    items: categories.slice(0, 6),
  },
  {
    id: "in-progress",
    title: "In Progress",
    items: [],
  },
  {
    id: "done",
    title: "Done",
    items: categories.slice(6),
  },
];

function App() {
  const [columns, setColumns] = useState(initialData);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeOrigin, setActiveOrigin] = useState<string | null>(null);
  const [activeOriginalIndex, setActiveOriginalIndex] = useState<number | null>(
    null
  );
  const [sideBackup, setSideBackup] = useState<{ [key: string]: string[] }>({});
  const [matchResult, setMatchResult] = useState<string | null>(null);

  // Helper: find which column contains an item.
  const findContainer = (id: string) =>
    columns.find((col) => col.items.includes(id))?.id;

  const handleDragStart = (event: DragStartEvent) => {
    const id = event.active.id as string;
    setActiveId(id);
    const origin = findContainer(id);
    setActiveOrigin(origin || null);
    if (origin === "todo" || origin === "done") {
      const col = columns.find((col) => col.id === origin);
      const index = col ? col.items.indexOf(id) : -1;
      setActiveOriginalIndex(index);
      setSideBackup((prev) => ({ ...prev, [origin]: [...(col?.items || [])] }));
    } else {
      setActiveOriginalIndex(null);
      setSideBackup({});
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) {
      clearActive();
      return;
    }
    const activeIdStr = active.id as string;
    const origin = activeOrigin;
    let targetContainer: string | null = null;
    if (columns.some((col) => col.id === over.id)) {
      targetContainer = over.id as string;
    } else {
      targetContainer = findContainer(over.id as string) || null;
    }
    if (!origin || !targetContainer) {
      clearActive();
      return;
    }
    // If dragged within the same container:
    if (origin === targetContainer) {
      if (origin === "in-progress") {
        setColumns((cols) =>
          cols.map((col) => {
            if (col.id === "in-progress") {
              const oldIndex = col.items.indexOf(activeIdStr);
              const newIndex = col.items.indexOf(over.id as string);
              if (newIndex === -1) return col;
              return {
                ...col,
                items: arrayMove(col.items, oldIndex, newIndex),
              };
            }
            return col;
          })
        );
      }
    } else {
      if (
        (origin === "todo" || origin === "done") &&
        targetContainer !== "in-progress"
      ) {
        clearActive();
        return;
      }
      setColumns((cols) => {
        let newColumns = cols.map((col) => {
          if (col.id === origin) {
            return {
              ...col,
              items: col.items.filter((item) => item !== activeIdStr),
            };
          }
          return col;
        });
        newColumns = newColumns.map((col) => {
          if (col.id === targetContainer) {
            if (over.id === targetContainer) {
              return { ...col, items: [...col.items, activeIdStr] };
            } else {
              const overIndex = col.items.indexOf(over.id as string);
              const newItems = [...col.items];
              newItems.splice(overIndex, 0, activeIdStr);
              return { ...col, items: newItems };
            }
          }
          return col;
        });
        return newColumns;
      });
    }
    clearActive();
  };

  const clearActive = () => {
    setActiveId(null);
    setActiveOrigin(null);
    setActiveOriginalIndex(null);
    setSideBackup({});
  };

  // Submission functionality: build ranking vector P from center column order.
  const handleSubmit = () => {
    const centerCol = columns.find((col) => col.id === "in-progress");
    const centerItems = centerCol ? centerCol.items : [];
    const P = categories.map((cat) => {
      const idx = centerItems.indexOf(cat);
      return idx !== -1 ? 12 - idx : 1;
    });
    const results = referenceData.map((ref) => {
      const totalDiff = ref.rankings.reduce(
        (sum, r, i) => sum + Math.abs(r - P[i]),
        0
      );
      return { crisis: ref.crisis, totalDiff };
    });
    const bestMatch = results.reduce((prev, curr) =>
      curr.totalDiff < prev.totalDiff ? curr : prev
    );
    setMatchResult(bestMatch.crisis);
    const crisisToPath: { [key: string]: string } = {
      "War in Gaza": "/gaza",
      "Crisis in Somalia": "/somalia",
      "Crisis in Haiti": "/haiti",
      "Crisis in South Sudan": "/south-sudan",
      "Mining in Africa": "/mining-in-africa",
      "Human Rights in Iran": "/iran",
      "Human Rights in China": "/china",
      "Human Rights in Uganda": "/uganda",
      "Amazon Rainforest": "/amazon-rainforest",
    };
    const path = crisisToPath[bestMatch.crisis];
    if (path) {
      window.location.href = "https://humanitarianimpactindex.org" + path;
    }
  };

  return (
    <div className="App">
      <DndContext
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="kanban-board">
          {columns.map((column) => (
            <SortableContext
              key={column.id}
              items={column.items}
              strategy={verticalListSortingStrategy}
            >
              <Column
                column={column}
                onSubmit={
                  column.id === "in-progress" ? handleSubmit : undefined
                }
              />
            </SortableContext>
          ))}
        </div>
        <DragOverlay>
          {activeId ? (
            <SortableItem
              id={activeId}
              isOverlay={true}
              inCenter={activeOrigin === "in-progress"}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      {matchResult && (
        <div
          style={{ textAlign: "center", marginTop: "20px", fontSize: "1.2em" }}
        >
          Closest Match: {matchResult}
        </div>
      )}
    </div>
  );
}

export default App;
